<!-- @format -->
<template>
    <div class="exit-survey-details-widget">
        <div class="widget-header">
            <h3>Exit Survey Details</h3>
            <button class="widget-close-button" @click="close">
                <widget-close-svg />
            </button>
        </div>

        <div class="content-wrapper">
            <div v-if="isLoading" class="loading-container">
                <loading-screen-svg />
            </div>
            <div v-else class="details-container">
                <h3>Account Details</h3>

                <div class="details-fields">
                    <div class="details-field">
                        <strong>Name</strong>
                        <div class="avatar-row">
                            <img
                                class="avatar"
                                :src="
                                    exitSurvey.clientLogoImageUrl ||
                                    require('@/assets/images/company-avatar.png')
                                "
                                alt="avatar"
                            />
                            <div>{{ exitSurvey.accountName }}</div>
                        </div>
                    </div>
                    <div class="details-field">
                        <strong>Who is Cancelling?</strong>
                        <div class="avatar-row">
                            <img
                                class="avatar"
                                :src="
                                    exitSurvey.cancelledByProfileImageUrl ||
                                    require('@/assets/images/avatar.png')
                                "
                                alt="avatar"
                            />
                            <div>
                                {{ exitSurvey.cancelledByFirstName }}
                                {{ exitSurvey.cancelledByLastName }}
                            </div>
                        </div>
                    </div>
                    <div class="details-field">
                        <strong>Client ID</strong>
                        <div>{{ exitSurvey.clientId }}</div>
                    </div>
                    <div class="details-field">
                        <strong>Vendor Subscription</strong>
                        <div>{{ exitSurvey.vendorSubscription ? 'Yes' : 'No' }}</div>
                    </div>
                    <div class="details-field">
                        <strong>Cancellation Date</strong>
                        <div>{{ accountCancelledAt }}</div>
                    </div>
                    <div class="details-field">
                        <strong>Account Created</strong>
                        <div>{{ accountCreatedAt }}</div>
                    </div>
                    <div class="details-field">
                        <strong>Numbers of Users</strong>
                        <div>{{ exitSurvey.accountUserCount }}</div>
                    </div>
                    <div class="details-field">
                        <strong>Total Paid</strong>
                        <div>{{ totalPaid }}</div>
                    </div>
                    <div class="details-field">
                        <strong>Location</strong>
                        <div>{{ location }}</div>
                    </div>
                    <div class="details-field">
                        <strong>Website</strong>
                        <div>{{ exitSurvey.accountWebsite }}</div>
                    </div>
                </div>

                <div class="divider"></div>

                <h3>Survey Responses</h3>

                <div class="details-fields">
                    <div class="details-field">
                        <strong>Reason for cancelling the plan?</strong>
                        <ul class="reasons-list">
                            <li v-if="exitSurvey.didntUseEnough">Didn't use GlassHive enough</li>
                            <li v-if="exitSurvey.missingFeatures">
                                We need features that GlassHive doesn't have
                            </li>
                            <li v-if="exitSurvey.tooDifficult">
                                GlassHive was too difficult to use
                            </li>
                            <li v-if="exitSurvey.tooExpensive">Too expensive</li>
                            <li v-if="exitSurvey.technicalIssues">Technical issues</li>
                            <li v-if="exitSurvey.movingSolutions">
                                We're moving to a different solution
                            </li>
                            <li v-if="exitSurvey.other">Other: {{ exitSurvey.otherReason }}</li>
                        </ul>
                    </div>
                    <div class="details-field">
                        <strong>Would you consider GlassHive again?</strong>
                        <div>{{ exitSurvey.considerAgain ? 'Yes' : 'No' }}</div>
                    </div>
                    <div class="details-field">
                        <strong>How can we make GlassHive even better? (optional)</strong>
                        <div>{{ exitSurvey.improvements }}</div>
                    </div>
                </div>
            </div>
        </div>

        <div class="widget-footer">
            <app-button class="cancel-button back-button" @click="close"> Close </app-button>
        </div>
    </div>
</template>

<script>
import EventBus from '@/scripts/event-bus';
import { EXIT_SURVEY_DETAILS_WIDGET_NAME } from '@/components/sliding-widgets/exit-survey-details-widget/exit-survey-details-widget.js';
import WidgetCloseSvg from '@/components/svg/quick-add/widget-close-svg.vue';
import { ExitSurveyService } from '@/scripts/services/exit-survey/exit-survey-service';

const LoadingScreenSvg = () => import('@/components/svg/loading-screen-svg.vue');
const AppButton = () => import('@/components/common/app-button.vue');

export default {
    name: EXIT_SURVEY_DETAILS_WIDGET_NAME,

    components: {
        'loading-screen-svg': LoadingScreenSvg,
        'widget-close-svg': WidgetCloseSvg,
        'app-button': AppButton,
    },

    props: {
        widget: {
            type: Object,
            required: true,
        },
    },

    data() {
        return {
            isLoading: false,
            exitSurvey: null,
        };
    },

    computed: {
        exitSurveyId() {
            return this.widget.config.id;
        },

        accountCancelledAt() {
            return this.formatDate(this.exitSurvey.accountCancelledAt);
        },

        accountCreatedAt() {
            return this.formatDate(this.exitSurvey.accountCreatedAt);
        },

        totalPaid() {
            let usd = new Intl.NumberFormat('en-US', {
                style: 'currency',
                currency: 'USD',
            });

            return usd.format(Number(this.exitSurvey.accountTotalPaid));
        },

        location() {
            let location = '';

            if (this.exitSurvey.accountAddress) {
                location += this.exitSurvey.accountAddress;
            }

            if (this.exitSurvey.accountAddress2) {
                location += ` ${this.exitSurvey.accountAddress2}`;
            }

            if (this.exitSurvey.accountCity) {
                location += `, ${this.exitSurvey.accountCity}`;
            }

            if (this.exitSurvey.accountState) {
                location += `, ${this.exitSurvey.accountState}`;
            }

            return location;
        },
    },

    async created() {
        this.isLoading = true;

        try {
            await this.loadExitSurvey();
        } catch (error) {
            this.$helpers.showFailNotification('Failed to load exit survey details', {
                error,
            });

            this.close();
        } finally {
            this.isLoading = false;
        }
    },

    methods: {
        async loadExitSurvey() {
            return ExitSurveyService.get(this.exitSurveyId).then((res) => {
                this.exitSurvey = res.data.data;
            });
        },

        close() {
            EventBus.$emit('close-widget', this.widget);
        },

        formatDate(date) {
            return this.$moment(date).format('ddd | MMM D, YYYY | h:mm:ss A').toUpperCase();
        },
    },
};
</script>

<style>
.exit-survey-details-widget .loading-container {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 100%;
    width: 100%;
}

.exit-survey-details-widget .divider {
    margin: 24px 0;
    border-bottom: 1px solid var(--color-border-divider);
}

.exit-survey-details-widget .details-container h3 {
    margin-bottom: 24px;
}

.exit-survey-details-widget .details-fields {
    display: flex;
    flex-direction: column;
    gap: 24px;
}

.exit-survey-details-widget .details-field {
    display: flex;
    flex-direction: column;
    gap: 8px;
}

.exit-survey-details-widget .details-field strong {
    font-weight: 600;
}

.exit-survey-details-widget .avatar-row {
    display: flex;
    align-items: center;
    gap: 8px;
}

.exit-survey-details-widget .avatar {
    border-radius: 50%;
    width: 32px;
    height: 32px;
}

.exit-survey-details-widget .reasons-list {
    display: flex;
    flex-direction: column;
    gap: 4px;
    list-style: disc;
    margin-bottom: 0;
    margin-left: 40px;
}
</style>
